import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  Avatar,
  Chip,
  Rating,
  useTheme,
  useMediaQuery,
  Skeleton,
} from '@mui/material';
import {
  Star as StarIcon,
  LocationOn as LocationIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import FavoriteButton from './FavoriteButton';

interface Caregiver {
  id: string;
  name: string;
  specialty: string;
  rating: number;
  reviewCount: number;
  location: string;
  avatar?: string;
  bio: string;
  verified: boolean;
  priceRange: string;
}

interface FeaturedCaregiversSectionProps {
  caregivers: Caregiver[];
  loading: boolean;
}

export const FeaturedCaregiversSection: React.FC<FeaturedCaregiversSectionProps> = ({ 
  caregivers, 
  loading 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  // Default featured caregivers if none are loaded
  const defaultCaregivers: Caregiver[] = [
    {
      id: '1',
      name: 'Dr. Sarah Williams',
      specialty: 'Physical Therapist',
      rating: 4.9,
      reviewCount: 127,
      location: 'San Francisco, CA',
      bio: 'Licensed PT with 8+ years experience in orthopedic rehabilitation and sports medicine.',
      verified: true,
      priceRange: '$120-150/hour',
    },
    {
      id: '2',
      name: 'Michael Rodriguez',
      specialty: 'Occupational Therapist',
      rating: 4.8,
      reviewCount: 89,
      location: 'Austin, TX',
      bio: 'Specialized in stroke recovery and adaptive equipment training for daily living.',
      verified: true,
      priceRange: '$100-130/hour',
    },
    {
      id: '3',
      name: 'Emily Chen',
      specialty: 'Senior Care Specialist',
      rating: 4.9,
      reviewCount: 156,
      location: 'Miami, FL',
      bio: 'Compassionate caregiver with expertise in dementia care and mobility assistance.',
      verified: true,
      priceRange: '$25-35/hour',
    },
    {
      id: '4',
      name: 'Dr. James Thompson',
      specialty: 'Home Health Nurse',
      rating: 4.7,
      reviewCount: 203,
      location: 'Seattle, WA',
      bio: 'RN with 12+ years in home health, specializing in post-surgical care and chronic conditions.',
      verified: true,
      priceRange: '$80-100/hour',
    },
    {
      id: '5',
      name: 'Lisa Park',
      specialty: 'Mental Health Counselor',
      rating: 4.8,
      reviewCount: 94,
      location: 'Denver, CO',
      bio: 'Licensed therapist providing individual and family counseling services.',
      verified: true,
      priceRange: '$90-120/hour',
    },
    {
      id: '6',
      name: 'Robert Johnson',
      specialty: 'Physical Therapist',
      rating: 4.9,
      reviewCount: 178,
      location: 'Chicago, IL',
      bio: 'Specialized in geriatric physical therapy and fall prevention programs.',
      verified: true,
      priceRange: '$110-140/hour',
    },
  ];

  const displayCaregivers = caregivers.length > 0 ? caregivers : defaultCaregivers;

  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase();
  };

  const handleViewProfile = (caregiverId: string) => {
    navigate(`/caretaker/${caregiverId}`);
  };

  const handleBrowseAll = () => {
    navigate('/search');
  };

  return (
    <Box sx={{ py: { xs: 8, md: 12 } }}>
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 'bold',
              color: theme.palette.text.primary,
              mb: 2,
            }}
          >
            Featured Caregivers
          </Typography>
          <Typography
            variant="h6"
            component="p"
            sx={{
              color: theme.palette.text.secondary,
              maxWidth: '600px',
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            Meet some of our top-rated, verified caregivers who are ready to provide 
            exceptional care tailored to your needs.
          </Typography>
        </Box>

        {/* Caregivers Grid */}
        <Grid container spacing={4}>
          {displayCaregivers.map((caregiver, index) => (
            <Grid item xs={12} sm={6} md={4} key={caregiver.id}>
              <Card
                elevation={2}
                sx={{
                  position: 'relative',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    elevation: 8,
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                {/* FEAT-057 — Save / Favorite Caregivers (MVP-18.29). On the
                    logged-out discovery grid, tapping the heart routes to the
                    auth page (auth-gate) via FavoriteButton. */}
                {!loading && (
                  <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
                    <FavoriteButton caregiverId={caregiver.id} size="small" />
                  </Box>
                )}

                {/* Avatar/Photo */}
                <Box
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    backgroundColor: theme.palette.grey[50],
                  }}
                >
                  {loading ? (
                    <Skeleton variant="circular" width={80} height={80} sx={{ mx: 'auto' }} />
                  ) : (
                    <Avatar
                      src={caregiver.avatar}
                      sx={{
                        width: 80,
                        height: 80,
                        mx: 'auto',
                        mb: 2,
                        backgroundColor: theme.palette.primary.main,
                        fontSize: '1.5rem',
                        fontWeight: 'bold',
                      }}
                    >
                      {!caregiver.avatar && getInitials(caregiver.name)}
                    </Avatar>
                  )}
                  
                  {loading ? (
                    <Skeleton variant="text" width="60%" height={24} sx={{ mx: 'auto' }} />
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 'bold',
                          color: theme.palette.text.primary,
                        }}
                      >
                        {caregiver.name}
                      </Typography>
                      {caregiver.verified && (
                        <VerifiedIcon
                          sx={{
                            color: theme.palette.success.main,
                            fontSize: '1.2rem',
                          }}
                        />
                      )}
                    </Box>
                  )}
                </Box>

                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  {/* Specialty */}
                  {loading ? (
                    <Skeleton variant="text" width="80%" height={24} sx={{ mb: 1 }} />
                  ) : (
                    <Chip
                      label={caregiver.specialty}
                      size="small"
                      sx={{
                        mb: 2,
                        backgroundColor: theme.palette.primary.light,
                        color: 'white',
                        fontWeight: 'bold',
                      }}
                    />
                  )}

                  {/* Rating */}
                  {loading ? (
                    <Skeleton variant="text" width="60%" height={20} sx={{ mb: 1 }} />
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Rating
                        value={caregiver.rating}
                        readOnly
                        size="small"
                        precision={0.1}
                        icon={<StarIcon fontSize="inherit" />}
                        sx={{
                          '& .MuiRating-iconFilled': {
                            color: theme.palette.warning.main,
                          },
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          ml: 1,
                          color: theme.palette.text.secondary,
                        }}
                      >
                        {caregiver.rating} ({caregiver.reviewCount} reviews)
                      </Typography>
                    </Box>
                  )}

                  {/* Location */}
                  {loading ? (
                    <Skeleton variant="text" width="70%" height={16} sx={{ mb: 2 }} />
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <LocationIcon
                        sx={{
                          fontSize: '1rem',
                          color: theme.palette.text.secondary,
                          mr: 0.5,
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.secondary,
                        }}
                      >
                        {caregiver.location}
                      </Typography>
                    </Box>
                  )}

                  {/* Bio */}
                  {loading ? (
                    <>
                      <Skeleton variant="text" height={40} sx={{ mb: 1 }} />
                      <Skeleton variant="text" height={40} />
                    </>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        lineHeight: 1.5,
                        mb: 2,
                      }}
                    >
                      {caregiver.bio}
                    </Typography>
                  )}

                  {/* Price Range */}
                  {loading ? (
                    <Skeleton variant="text" width="50%" height={20} />
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.primary.main,
                        fontWeight: 'bold',
                      }}
                    >
                      {caregiver.priceRange}
                    </Typography>
                  )}
                </CardContent>

                <CardActions sx={{ p: 3, pt: 0 }}>
                  {loading ? (
                    <Skeleton variant="rectangular" width="100%" height={36} />
                  ) : (
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => handleViewProfile(caregiver.id)}
                      sx={{
                        backgroundColor: theme.palette.primary.main,
                        '&:hover': {
                          backgroundColor: theme.palette.primary.dark,
                        },
                      }}
                    >
                      View Profile
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Bottom CTA */}
        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.text.secondary,
              mb: 3,
            }}
          >
            Looking for a specific type of caregiver?
          </Typography>
          <Button
            variant="outlined"
            size="large"
            onClick={handleBrowseAll}
            sx={{
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              px: 4,
              py: 1.5,
              '&:hover': {
                borderColor: theme.palette.primary.dark,
                backgroundColor: theme.palette.primary.light,
                color: 'white',
              },
            }}
          >
            Browse All Caregivers
          </Button>
        </Box>
      </Container>
    </Box>
  );
};
